import { AxiosResponse } from 'axios';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { DownloadProgress, downloadStreamedCsv } from './streaming-download';

/**
 * One selectable APS instrument. These serials live only in the APS database,
 * not the device registry, so the picker is populated from here.
 */
export type ApsSerial = {
  serial: string;
  /** Table backing this serial: "drecord" or "bins_d". */
  source: string;
  sampleCount: number;
  firstSample: number | null;
  lastSample: number | null;
};

/**
 * Time-bucketed APS channel rates.
 * `values[i][c]` = mean count rate of channel c during the bucket starting at
 * `timestamps[i]`; null = no sample covered it (gap, not zero).
 */
export type ApsHistogramResponse = {
  serial: string;
  source: string;
  startDate: number;
  endDate: number;
  interval: string;
  intervalMillis: number;
  channelCount: number;
  timestamps: number[];
  values: (number | null)[][];
};

/** Box-plot statistics for one APS channel, in counts/s. */
export type ApsChannelStats = {
  /** Zero-based channel index, matching the charts' bin indexing. */
  bin: number;
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  p05: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p95: number | null;
};

export type ApsDistributionResponse = {
  serial: string;
  source: string;
  startDate: number;
  endDate: number;
  sampleCount: number;
  channelCount: number;
  channels: ApsChannelStats[];
};

const BASE = `${DEV_NEW_DOMAIN_URL}/aps-channels`;

/**
 * List every APS instrument with data, newest first.
 * @returns Axios response with the available serials.
 */
export const getApsSerials = (): Promise<AxiosResponse<ApsSerial[]>> => {
  return axiosInstance.get(`${BASE}/serials`);
};

/**
 * Fetch the time × channel count-rate matrix for the contour plot.
 * @param serial - Instrument serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @param interval - Optional fixed interval like "1h"; server auto-picks when omitted.
 * @returns Axios response with the matrix.
 */
export const getApsHistogram = (
  serial: string,
  startDate: number,
  endDate: number,
  interval?: string
): Promise<AxiosResponse<ApsHistogramResponse>> => {
  return axiosInstance.get(`${BASE}/histogram`, {
    params: { serial, startDate, endDate, ...(interval ? { interval } : {}) }
  });
};

/**
 * Fetch per-channel distribution statistics for the box plot.
 * @param serial - Instrument serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @returns Axios response with per-channel stats.
 */
export const getApsDistribution = (
  serial: string,
  startDate: number,
  endDate: number
): Promise<AxiosResponse<ApsDistributionResponse>> => {
  return axiosInstance.get(`${BASE}/distribution`, {
    params: { serial, startDate, endDate }
  });
};

/**
 * Filename mirroring the one the export endpoint sets, computed client-side so
 * the save dialog can be opened before the request starts.
 * @param serial - Instrument serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @returns Suggested filename.
 */
function exportFilename(serial: string, startDate: number, endDate: number): string {
  /**
   * Date part of a timestamp, for the filename.
   * @param ms - Epoch millis.
   * @returns "YYYY-MM-DD".
   */
  const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
  return `aps-channels-${serial}-${day(startDate)}-to-${day(endDate)}.csv`;
}

/**
 * Download every complete sample in the window as CSV (raw counts per channel).
 *
 * Streamed by the server and written straight to disk. See
 * {@link downloadStreamedCsv}.
 * @param serial - Instrument serial number.
 * @param startDate - Window start, epoch millis.
 * @param endDate - Window end, epoch millis.
 * @param onProgress - Optional callback receiving bytes downloaded so far.
 * @returns Resolves once the file is written, or immediately if the user cancels.
 */
export const downloadApsCsv = async (
  serial: string,
  startDate: number,
  endDate: number,
  onProgress?: DownloadProgress
): Promise<void> => {
  const params = new URLSearchParams({
    serial,
    startDate: String(startDate),
    endDate: String(endDate)
  });
  await downloadStreamedCsv(
    `${BASE}/export?${params}`,
    exportFilename(serial, startDate, endDate),
    onProgress
  );
};
